"use client";

import type { EmblaCarouselType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import type { MotionValue } from "framer-motion";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";

import { muxAnimatedPreview } from "@/components/customers/customer-story-videos";
import { OurCustomersHeroRiveBackground } from "@/components/customers/hero-rive-background";
import { Dialog, DialogClose, DialogTitle } from "@/components/ui/dialog";
import { GridElbow, GridTee } from "@/components/ui/grid-connector";
import { ScrollRevealText } from "@/components/ui/scroll-reveal-text";
import {
  VideoDialogPopup,
  videoDialogCloseCompactClass,
  videoDialogOverlayDarkClass,
} from "@/components/ui/video-dialog";
import { cn } from "@/lib/utils";

const MuxPlayer = dynamic(
  () =>
    import("@/components/post-rich-text/mux-player").then(
      (module) => module.MuxPlayer
    ),
  { ssr: false }
);

export interface HeroEpisode {
  tag: string;
  title: string;
  description: string;
  href: string;
  playbackId: string;
  image: { src: string; alt: string };
}

interface InteractiveHeroProps {
  headlineMobile: React.ReactNode;
  headlineDesktop: React.ReactNode;
  watchLabel: string;
  prevLabel: string;
  nextLabel: string;
  episodes: HeroEpisode[];
  /** Carousel start index — defaults to the latest episode. */
  initialIndex?: number;
  className?: string;
}

// Scrubber bars — uniform ticks; fill grows left→right (clip-path). Gradient
// is stretched across whatever portion is filled (≈33% / 67% / 100% per episode).
const SCRUBBER_BARS = 75;
/** Unplayed tick color — lighter than Figma #313335 so episode markers read at a glance. */
const SCRUBBER_UNPLAYED = "#525558";
const SCRUBBER_COLORS = [
  "#001EFF",
  "#411DFF",
  "#6B20FC",
  "#8125F6",
  "#9D2DEA",
  "#AF32E0",
  "#BF36D5",
  "#D53FC3",
  "#E942A3",
  "#F74B81",
  "#FE634D",
];

/** Smooth scrubber fill when snapping or looping (e.g. 100% → 33%). */
const SCRUBBER_SPRING = { stiffness: 100, damping: 26, mass: 0.45 } as const;

/** Linear-style content reveal — opacity only, GPU-friendly. */
const HERO_REVEAL_MS = 280;
const heroRevealClass = (ready: boolean) =>
  cn(
    "transition-opacity ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none",
    ready ? "opacity-100" : "opacity-0"
  );

function useCarouselPositioned(emblaApi: EmblaCarouselType | undefined) {
  const [positioned, setPositioned] = useState(false);
  const hasMarkedRef = useRef(false);

  useEffect(() => {
    if (!emblaApi || hasMarkedRef.current) return;

    const markPositioned = () => {
      if (hasMarkedRef.current) return;
      hasMarkedRef.current = true;
      // Double rAF: Embla sets scroll offset in the same frame as init.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setPositioned(true);
        });
      });
    };

    emblaApi.on("init", markPositioned);
    if (emblaApi.scrollSnapList().length > 0) {
      markPositioned();
    }

    return () => {
      emblaApi.off("init", markPositioned);
    };
  }, [emblaApi]);

  return positioned;
}

const CAROUSEL_SLIDE_SHELL_CLASS =
  "min-w-0 flex-[0_0_88%] px-2 sm:flex-[0_0_77%] lg:px-8";
const CAROUSEL_CARD_SHELL_CLASS =
  "relative aspect-[327/220] overflow-hidden rounded-2xl lg:aspect-[1040/548]";

/** Shortest signed distance from scroll position to a snap (loop-aware). */
function diffToSnap(
  api: EmblaCarouselType,
  snapProgress: number,
  scrollProgress: number
) {
  let diff = snapProgress - scrollProgress;
  const engine = api.internalEngine();
  if (!engine.options.loop) return diff;

  engine.slideLooper.loopPoints.forEach((loopItem) => {
    const target = loopItem.target();
    if (target === 0) return;
    const sign = Math.sign(target);
    if (sign === -1) {
      diff = snapProgress - (1 + scrollProgress);
    }
    if (sign === 1) {
      diff = snapProgress + (1 - scrollProgress);
    }
  });

  return diff;
}

/**
 * Map carousel position → scrubber fill 0→1 aligned to episodes:
 * Ep1 (index 0) → 1/N, … latest episode → 1. Matches Next→forward / Prev→backward.
 */
function playlistProgress(api: EmblaCarouselType, episodeCount: number) {
  const snaps = api.scrollSnapList();
  const count = episodeCount > 0 ? episodeCount : snaps.length;
  if (count <= 1) return 1;

  const scrollProgress = api.scrollProgress();
  const selected = api.selectedScrollSnap();
  const normalizedIndex = ((selected % count) + count) % count;

  for (let i = 0; i < count - 1; i++) {
    const start = snaps[i]!;
    const end = snaps[i + 1]!;
    const diffStart = diffToSnap(api, start, scrollProgress);
    const diffEnd = diffToSnap(api, end, scrollProgress);

    // Between this snap and the next (scroll moving forward in snap order).
    if (diffStart <= 0 && diffEnd >= 0) {
      const span = diffEnd - diffStart;
      const t = span === 0 ? 0 : -diffStart / span;
      return Math.min(1, Math.max(0, (i + t + 1) / count));
    }
  }

  // At a snap or ambiguous loop wrap — index is the reliable signal.
  return (normalizedIndex + 1) / count;
}

function scrubberBarBackground(
  barIndex: number,
  progress: number,
  played: boolean
): string {
  if (!played) return SCRUBBER_UNPLAYED;

  const fillEnd = progress * SCRUBBER_BARS;
  if (barIndex >= fillEnd || fillEnd <= 0) return SCRUBBER_UNPLAYED;

  // Map the filled span left→right onto the Figma 11-step gradient.
  const t = (barIndex + 0.5) / fillEnd;
  const colorIndex = Math.min(
    SCRUBBER_COLORS.length - 1,
    Math.floor(t * SCRUBBER_COLORS.length)
  );
  return SCRUBBER_COLORS[colorIndex] ?? SCRUBBER_UNPLAYED;
}

export function InteractiveHero({
  headlineMobile,
  headlineDesktop,
  watchLabel,
  prevLabel,
  nextLabel,
  episodes,
  initialIndex = Math.max(0, episodes.length - 1),
  className,
}: InteractiveHeroProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    axis: "x",
    containScroll: false,
    dragThreshold: 12,
    startIndex: initialIndex,
  });
  const carouselPositioned = useCarouselPositioned(emblaApi);
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const [watchDialogOpen, setWatchDialogOpen] = useState(false);
  const initialScrubberFill =
    episodes.length > 0 ? (initialIndex + 1) / episodes.length : 0;
  const scrubberTarget = useMotionValue(initialScrubberFill);
  const scrubberProgress = useSpring(scrubberTarget, SCRUBBER_SPRING);

  const activeEpisode = episodes[selectedIndex] ?? episodes[initialIndex];

  const onSelect = useCallback((api: NonNullable<typeof emblaApi>) => {
    setSelectedIndex(api.selectedScrollSnap());
  }, []);

  const episodeCount = episodes.length;

  const syncScrubberProgress = useCallback(
    (api: NonNullable<typeof emblaApi>) => {
      scrubberTarget.set(
        Math.min(1, Math.max(0, playlistProgress(api, episodeCount)))
      );
    },
    [scrubberTarget, episodeCount]
  );

  useEffect(() => {
    if (!emblaApi) return;
    const onUpdate = () => {
      onSelect(emblaApi);
      syncScrubberProgress(emblaApi);
    };
    onUpdate();
    emblaApi.on("scroll", onUpdate);
    emblaApi.on("select", onUpdate);
    emblaApi.on("reInit", onUpdate);
    return () => {
      emblaApi.off("scroll", onUpdate);
      emblaApi.off("select", onUpdate);
      emblaApi.off("reInit", onUpdate);
    };
  }, [emblaApi, onSelect, syncScrubberProgress]);

  const scrollToPreviousEpisode = useCallback(
    () => emblaApi?.scrollPrev(),
    [emblaApi]
  );
  const scrollToNextEpisode = useCallback(
    () => emblaApi?.scrollNext(),
    [emblaApi]
  );
  const scrollToEpisode = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi]
  );
  const openWatchDialog = useCallback(() => setWatchDialogOpen(true), []);

  return (
    <section
      data-hero-overlay-nav
      data-testid="our-customers-hero-section"
      className={cn(
        "color-block bg-grey-950 relative [overflow-anchor:none]",
        className
      )}
    >
      {/* Mobile hero (Figma node 5616-20383): purple/blue radial glows + a grid
          of faint glassy logo cards bleeding off the edges behind a bottom fade,
          with the 40px title 64px below nav and a matching gap to the carousel. */}
      <MobileHero headline={headlineMobile} />

      {/* Desktop grid hero — the exact Figma background (node 6323-30291: glows +
          grid with per-square opacity + names + bottom fade, title stripped)
          shown full-width so the whole 1440 design scales as one unit, like the
          Figma frame. The live, localized <h1> is overlaid and scales with it. */}
      <DesktopHeroGrid headline={headlineDesktop} />

      {/* divider line under the grid section (Figma Line 613, #191A1A) */}
      <div aria-hidden className="h-px w-full bg-[#191a1a]" />

      {/* Video reel — rails + ⊤ tees sit on the divider (Figma 6323:30422/45 at
          y=562); content padding is inner so lines are not pushed down. */}
      <div
        data-testid="our-customers-hero-reel-section"
        className="relative pb-12 lg:pb-20"
      >
        {/* vertical grid lines (Figma Line 609/610, #191A1A) */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-[11.67%] hidden w-px bg-[#191a1a] lg:block"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-[88.33%] hidden w-px bg-[#191a1a] lg:block"
        />
        {/* white ⊤ tees where the verticals meet the top divider (7×4) */}
        <GridTee className="text-grey-950 pointer-events-none absolute -top-[2px] left-[11.67%] z-10 hidden -translate-x-1/2 text-white lg:block" />
        <GridTee className="text-grey-950 pointer-events-none absolute -top-[2px] left-[88.33%] z-10 hidden -translate-x-1/2 text-white lg:block" />

        <div className="relative pt-0 lg:pt-12">
          {!carouselPositioned && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 z-[1] lg:top-12"
            >
              <div className="flex justify-center">
                <div className={CAROUSEL_SLIDE_SHELL_CLASS}>
                  <div
                    className={cn(CAROUSEL_CARD_SHELL_CLASS, "bg-[#171717]")}
                  />
                </div>
              </div>
            </div>
          )}

          <div
            ref={emblaRef}
            className={cn(
              "touch-pan-y touch-pinch-zoom overflow-hidden",
              heroRevealClass(carouselPositioned)
            )}
            style={{ transitionDuration: `${HERO_REVEAL_MS}ms` }}
          >
            <div className="flex">
              {episodes.map((episode, index) => {
                const isActive = index === selectedIndex;
                const previousEpisodeIndex =
                  (selectedIndex - 1 + episodes.length) % episodes.length;
                const sideLabel =
                  index === previousEpisodeIndex ? prevLabel : nextLabel;
                return (
                  <div
                    key={episode.playbackId}
                    className={CAROUSEL_SLIDE_SHELL_CLASS}
                  >
                    {/* Peeks start transparent and come to full opacity on hover
                        (Figma interaction note); the active card is always full. */}
                    <CarouselSlideCard
                      isActive={isActive}
                      sideLabel={sideLabel}
                      onNavigate={() => scrollToEpisode(index)}
                    >
                      <HeroEpisodeMedia
                        episode={episode}
                        isActive={isActive}
                        preload={index === initialIndex}
                      />
                      {/* overlay + content only on the active card (peeks show
                        just the image, per Figma) */}
                      {isActive && (
                        <>
                          <div className="absolute inset-x-0 bottom-0 h-[67.5%] bg-gradient-to-b from-transparent to-black" />
                          <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-5 lg:gap-6 lg:px-6 lg:pb-5">
                            <div className="flex max-w-[484px] flex-col gap-3 lg:gap-[17px]">
                              <span className="text-body-xs w-fit rounded-[4px] bg-white/20 p-1 font-medium text-white">
                                {episode.tag}
                              </span>
                              <h2 className="lg:text-heading-24 text-[16px] leading-[1.5] font-normal tracking-[-0.32px] text-white lg:leading-[1.2] lg:tracking-[-0.96px]">
                                {episode.title}
                              </h2>
                              <p className="text-body-sm hidden leading-[1.5] tracking-[-0.28px] text-white lg:block">
                                {episode.description}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={openWatchDialog}
                              className="group/watch flex shrink-0 items-center gap-3"
                              aria-label={`${watchLabel} — ${episode.title}`}
                            >
                              <span className="text-body-sm tracking-[-0.28px] whitespace-nowrap text-[#b0b3b5]">
                                {watchLabel}
                              </span>
                              <WatchNowIcon className="size-10 shrink-0 transition-opacity group-hover/watch:opacity-90" />
                            </button>
                          </div>
                        </>
                      )}
                    </CarouselSlideCard>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Mobile controls (Figma 5932-26690): the scrubber on its own
            full-width row, then Prev | Next split 50/50 by a vertical divider. */}
        <div className="mt-6 lg:hidden">
          <div className="border-y border-[#191a1a] px-6 py-[18px]">
            <Scrubber
              progress={scrubberProgress}
              episodeCount={episodes.length}
              onEpisodeSelect={scrollToEpisode}
            />
          </div>
          <div className="grid grid-cols-2 divide-x divide-[#191a1a] border-b border-[#191a1a]">
            <button
              type="button"
              onClick={scrollToPreviousEpisode}
              className="text-body-xs py-5 text-center font-normal tracking-[-0.24px] text-[#b0b3b5] transition-colors hover:text-white"
            >
              {prevLabel}
            </button>
            <button
              type="button"
              onClick={scrollToNextEpisode}
              className="text-body-xs py-5 text-center font-normal tracking-[-0.24px] text-[#b0b3b5] transition-colors hover:text-white"
            >
              {nextLabel}
            </button>
          </div>
        </div>

        {/* Desktop controls — Prev | scrubber | Next in one row. Top/bottom
            borders are the Figma horizontal lines (#191A1A); Prev/Next are
            centred in the outer margin columns. */}
        <div className="relative mt-12 hidden items-stretch border-y border-[#191a1a] lg:flex">
          {/* white corner connectors at the four corners of the controls box,
              each pointing inward */}
          <GridElbow
            corner="top-left"
            className="text-grey-950 pointer-events-none absolute -top-[1.5px] left-[11.67%] z-10 -translate-x-1/2 text-white"
          />
          <GridElbow
            corner="top-right"
            className="text-grey-950 pointer-events-none absolute -top-[1.5px] left-[88.33%] z-10 -translate-x-1/2 text-white"
          />
          <GridElbow
            corner="bottom-left"
            className="text-grey-950 pointer-events-none absolute -bottom-[1.5px] left-[11.67%] z-10 -translate-x-1/2 text-white"
          />
          <GridElbow
            corner="bottom-right"
            className="text-grey-950 pointer-events-none absolute -bottom-[1.5px] left-[88.33%] z-10 -translate-x-1/2 text-white"
          />
          {/* Prev — centred in the left margin column (before the left line) */}
          <button
            type="button"
            onClick={scrollToPreviousEpisode}
            className="text-body-xs flex w-[11.67%] shrink-0 items-center justify-center py-6 font-normal tracking-[-0.24px] text-[#b0b3b5] transition-colors hover:text-white"
          >
            {prevLabel}
          </button>
          <Scrubber
            progress={scrubberProgress}
            episodeCount={episodes.length}
            onEpisodeSelect={scrollToEpisode}
            className="flex-1 px-8 py-6"
          />
          {/* Next — centred in the right margin column (after the right line) */}
          <button
            type="button"
            onClick={scrollToNextEpisode}
            className="text-body-xs flex w-[11.67%] shrink-0 items-center justify-center py-6 font-normal tracking-[-0.24px] text-[#b0b3b5] transition-colors hover:text-white"
          >
            {nextLabel}
          </button>
        </div>

        {activeEpisode ? (
          <Dialog open={watchDialogOpen} onOpenChange={setWatchDialogOpen}>
            <VideoDialogPopup
              variant="card"
              overlayClassName={videoDialogOverlayDarkClass}
            >
              <DialogTitle className="sr-only">
                {activeEpisode.title}
              </DialogTitle>
              {watchDialogOpen ? (
                <MuxPlayer
                  key={activeEpisode.playbackId}
                  playbackId={activeEpisode.playbackId}
                  title={activeEpisode.title}
                  autoPlay="any"
                  loading="page"
                  preload="auto"
                  className="aspect-video w-full"
                />
              ) : null}
              <DialogClose className={videoDialogCloseCompactClass}>
                Close
              </DialogClose>
            </VideoDialogPopup>
          </Dialog>
        ) : null}
      </div>
    </section>
  );
}

// Desktop grid hero — Rive background (Figma node 6323-30291) with live
// localized headline overlaid. The 1440×562 artboard scales as one unit.
function DesktopHeroGrid({ headline }: { headline: React.ReactNode }) {
  const [bgReady, setBgReady] = useState(false);

  return (
    <div
      data-testid="our-customers-hero-content-desktop"
      className="relative hidden aspect-[1440/562] w-full overflow-hidden bg-[#0c0d0d] lg:block"
    >
      <OurCustomersHeroRiveBackground
        variant="desktop"
        onReady={() => setBgReady(true)}
        className={cn(heroRevealClass(bgReady))}
        style={{ transitionDuration: `${HERO_REVEAL_MS}ms` }}
      />

      {/* Bottom fade over grid (Figma Hero Content node 6323:30419). */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-x-0 top-[61%] z-[1] h-[39%] bg-gradient-to-b from-[#0c0d0d]/0 via-[#0c0d0d]/50 via-[43.099%] to-[#0c0d0d]",
          heroRevealClass(bgReady)
        )}
        style={{ transitionDuration: `${HERO_REVEAL_MS}ms` }}
      />

      <h1
        className={cn(
          "absolute top-[61%] left-1/2 z-10 w-full max-w-[52.08vw] -translate-x-1/2 -translate-y-1/2 text-center text-[4.44vw] leading-[1.1] font-normal tracking-[-0.04em]",
          heroRevealClass(bgReady)
        )}
        style={{ transitionDuration: `${HERO_REVEAL_MS}ms` }}
      >
        {typeof headline === "string" ? (
          <ScrollRevealText variant="dark">{headline}</ScrollRevealText>
        ) : (
          headline
        )}
      </h1>
    </div>
  );
}

// Active card is a static shell; inactive peek panels are buttons (click + pointer).
function CarouselSlideCard({
  isActive,
  sideLabel,
  onNavigate,
  children,
}: {
  isActive: boolean;
  sideLabel: string;
  onNavigate: () => void;
  children: React.ReactNode;
}) {
  const shellClass = cn(
    CAROUSEL_CARD_SHELL_CLASS,
    "transition-opacity duration-500",
    isActive ? "opacity-100" : "cursor-pointer opacity-10 hover:opacity-100"
  );

  if (isActive) {
    return <div className={shellClass}>{children}</div>;
  }

  return (
    <button
      type="button"
      aria-label={sideLabel}
      onClick={onNavigate}
      className={cn(
        shellClass,
        "block w-full border-0 bg-transparent p-0 text-left"
      )}
    >
      {children}
    </button>
  );
}

function HeroEpisodeMedia({
  episode,
  isActive,
  preload,
}: {
  episode: HeroEpisode;
  isActive: boolean;
  preload: boolean;
}) {
  const [posterLoaded, setPosterLoaded] = useState(false);

  return (
    <>
      <HeroEpisodeImage
        src={episode.image.src}
        alt={episode.image.alt}
        preload={preload}
        onLoad={() => setPosterLoaded(true)}
      />
      {isActive && posterLoaded ? (
        <EpisodeVideoPreview playbackId={episode.playbackId} />
      ) : null}
    </>
  );
}

function HeroEpisodeImage({
  src,
  alt,
  preload = false,
  onLoad,
}: {
  src: string;
  alt: string;
  preload?: boolean;
  onLoad?: () => void;
}) {
  const [loaded, setLoaded] = useState(false);

  const handleLoad = () => {
    setLoaded(true);
    onLoad?.();
  };

  return (
    <>
      <div aria-hidden className="absolute inset-0 bg-[#171717]" />
      <Image
        src={src}
        alt={alt}
        fill
        preload={preload}
        sizes="(min-width: 1024px) 72vw, 88vw"
        onLoad={handleLoad}
        className={cn("object-cover", heroRevealClass(loaded))}
        style={{ transitionDuration: `${HERO_REVEAL_MS}ms` }}
      />
    </>
  );
}

/** Mux animated preview for the centred episode card (first ~7s, loops). */
function EpisodeVideoPreview({ playbackId }: { playbackId: string }) {
  const [loaded, setLoaded] = useState(false);

  return (
    // Plain img — Next/Image optimization strips animation from Mux animated.webp.
    <img
      src={muxAnimatedPreview(playbackId)}
      alt=""
      aria-hidden
      decoding="async"
      fetchPriority="low"
      onLoad={() => setLoaded(true)}
      className={cn(
        "pointer-events-none absolute inset-0 size-full object-cover motion-reduce:hidden",
        heroRevealClass(loaded)
      )}
      style={{ transitionDuration: `${HERO_REVEAL_MS}ms` }}
    />
  );
}

// Mobile hero background (Figma node 5616-20383). Same Rive asset as desktop,
// cropped to the right section; live headline overlaid with bottom fade.
function MobileHero({ headline }: { headline: React.ReactNode }) {
  const [bgReady, setBgReady] = useState(false);

  return (
    <div
      data-testid="our-customers-hero-content-mobile"
      className="relative overflow-hidden bg-[#0c0d0d] lg:hidden"
    >
      <OurCustomersHeroRiveBackground
        variant="mobile"
        onReady={() => setBgReady(true)}
        className={cn("absolute inset-0", heroRevealClass(bgReady))}
        style={{ transitionDuration: `${HERO_REVEAL_MS}ms` }}
      />

      {/* bottom fade over the grid — anchored to the shortened hero frame */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-[120px] bg-gradient-to-b from-[#0c0d0d]/0 via-[#0c0d0d]/50 via-[43%] to-[#0c0d0d]",
          heroRevealClass(bgReady)
        )}
        style={{ transitionDuration: `${HERO_REVEAL_MS}ms` }}
      />

      {/* Headline keeps the original 64px nav gap; pb-16 mirrors it to the carousel. */}
      <div
        className={cn(
          "relative z-10 px-6 pt-[calc(var(--nav-offset)+64px)] pb-16",
          heroRevealClass(bgReady)
        )}
        style={{ transitionDuration: `${HERO_REVEAL_MS}ms` }}
      >
        <h1 className="mx-auto w-[327px] max-w-[87%] text-center text-[40px] leading-[1.2] font-normal tracking-[-1.6px]">
          {typeof headline === "string" ? (
            <ScrollRevealText variant="dark">{headline}</ScrollRevealText>
          ) : (
            headline
          )}
        </h1>
      </div>
    </div>
  );
}

// Reel scrubber — left-to-right fill via clip on the played tick row (see home Waveform).
function Scrubber({
  className,
  progress,
  episodeCount,
  onEpisodeSelect,
}: {
  className?: string;
  progress: MotionValue<number>;
  episodeCount: number;
  onEpisodeSelect?: (index: number) => void;
}) {
  const clipPath = useTransform(
    progress,
    (p) => `inset(0 ${(1 - Math.min(1, Math.max(0, p))) * 100}% 0 0)`
  );
  const interactive = Boolean(episodeCount > 0 && onEpisodeSelect);

  const handleTrackClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!interactive || !onEpisodeSelect) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const segmentIndex = Math.min(
      episodeCount - 1,
      Math.max(0, Math.floor(x * episodeCount))
    );
    // Fill grows left→right; Ep1 is left, latest episode is right.
    onEpisodeSelect(segmentIndex);
  };

  const barRows = (
    <>
      <ScrubberBarRow className="absolute inset-0" />
      <motion.div className="absolute inset-0" style={{ clipPath }}>
        <ScrubberPlayedBarRow
          className="absolute inset-0"
          progress={progress}
        />
      </motion.div>
    </>
  );

  return (
    <div
      className={cn(
        "flex items-center gap-[7px] overflow-hidden lg:justify-between lg:gap-0",
        className
      )}
    >
      {interactive ? (
        <button
          type="button"
          onClick={handleTrackClick}
          aria-label="Episode progress"
          className="relative h-7 w-full min-w-0 cursor-pointer border-0 bg-transparent p-0 lg:h-8"
        >
          {barRows}
        </button>
      ) : (
        <div aria-hidden className="relative h-7 w-full min-w-0 lg:h-8">
          {barRows}
        </div>
      )}
    </div>
  );
}

function ScrubberBarRow({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "flex items-center gap-[7px] overflow-hidden lg:justify-between lg:gap-0",
        className
      )}
    >
      {Array.from({ length: SCRUBBER_BARS }).map((_, i) => (
        <span
          key={i}
          className="h-7 w-[2px] shrink-0 rounded-full lg:h-8 lg:w-[3px]"
          style={{ background: SCRUBBER_UNPLAYED }}
        />
      ))}
    </div>
  );
}

function ScrubberPlayedBarRow({
  className,
  progress,
}: {
  className?: string;
  progress: MotionValue<number>;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-[7px] overflow-hidden lg:justify-between lg:gap-0",
        className
      )}
    >
      {Array.from({ length: SCRUBBER_BARS }).map((_, i) => (
        <ScrubberPlayedBar key={i} barIndex={i} progress={progress} />
      ))}
    </div>
  );
}

function ScrubberPlayedBar({
  barIndex,
  progress,
}: {
  barIndex: number;
  progress: MotionValue<number>;
}) {
  const background = useTransform(progress, (p) =>
    scrubberBarBackground(barIndex, p, true)
  );

  return (
    <motion.span
      className="h-7 w-[2px] shrink-0 rounded-full lg:h-8 lg:w-[3px]"
      style={{ background }}
    />
  );
}

// "Watch now" play button — exact Figma icon (node 6323-30438): a squircle at
// white/0.2 with a white/0.2 hairline border and a rounded-tip play triangle.
function WatchNowIcon({ className }: { className?: string }) {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className={className}
    >
      <path
        d="M0 19.2C0 12.4794 0 9.11905 1.30792 6.55211C2.4584 4.29417 4.29417 2.4584 6.55211 1.30792C9.11905 0 12.4794 0 19.2 0H20.8C27.5206 0 30.8809 0 33.4479 1.30792C35.7058 2.4584 37.5416 4.29417 38.6921 6.55211C40 9.11905 40 12.4794 40 19.2V20.8C40 27.5206 40 30.8809 38.6921 33.4479C37.5416 35.7058 35.7058 37.5416 33.4479 38.6921C30.8809 40 27.5206 40 20.8 40H19.2C12.4794 40 9.11905 40 6.55211 38.6921C4.29417 37.5416 2.4584 35.7058 1.30792 33.4479C0 30.8809 0 27.5206 0 20.8V19.2Z"
        fill="white"
        fillOpacity="0.2"
      />
      <path
        d="M19.2002 0.5H20.7998C24.1683 0.5 26.6647 0.500686 28.6406 0.662109C30.6107 0.823073 32.0195 1.14185 33.2207 1.75391C35.3845 2.85644 37.1436 4.61547 38.2461 6.7793C38.8582 7.98053 39.1769 9.38927 39.3379 11.3594C39.4993 13.3353 39.5 15.8317 39.5 19.2002V20.7998C39.5 24.1683 39.4993 26.6647 39.3379 28.6406C39.1769 30.6107 38.8582 32.0195 38.2461 33.2207C37.1436 35.3845 35.3845 37.1436 33.2207 38.2461C32.0195 38.8582 30.6107 39.1769 28.6406 39.3379C26.6647 39.4993 24.1683 39.5 20.7998 39.5H19.2002C15.8317 39.5 13.3353 39.4993 11.3594 39.3379C9.38927 39.1769 7.98053 38.8582 6.7793 38.2461C4.61547 37.1436 2.85644 35.3845 1.75391 33.2207C1.14185 32.0195 0.823073 30.6107 0.662109 28.6406C0.500686 26.6647 0.5 24.1683 0.5 20.7998V19.2002C0.5 15.8317 0.500686 13.3353 0.662109 11.3594C0.823073 9.38927 1.14185 7.98053 1.75391 6.7793C2.85644 4.61547 4.61547 2.85644 6.7793 1.75391C7.98053 1.14185 9.38927 0.823073 11.3594 0.662109C13.3353 0.500686 15.8317 0.5 19.2002 0.5Z"
        stroke="white"
        strokeOpacity="0.2"
      />
      <path
        d="M25.3748 18.433C26.2084 18.9072 26.2084 20.0928 25.3748 20.567L17.8757 24.8331C17.042 25.3073 16 24.7145 16 23.7661L16 15.2339C16 14.2855 17.042 13.6927 17.8757 14.1669L25.3748 18.433Z"
        fill="white"
      />
    </svg>
  );
}
